import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { MeResponse } from "@wmart/shared-types";
import { api, ApiError } from "./api-client.js";

export type { MeResponse };

interface AuthState {
  status: "loading" | "authed" | "anon";
  me: MeResponse | null;
  /**
   * Re-fetch `/v1/auth/me` and update context. Returns the freshly-fetched
   * `MeResponse` on success (so callers — e.g. the dual-kind Login — can
   * branch on `kind` without racing the async `me` state update), or `null`
   * when the session is anonymous (401).
   */
  refresh: () => Promise<MeResponse | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [me, setMe] = useState<MeResponse | null>(null);

  async function refresh(): Promise<MeResponse | null> {
    try {
      const data = await api.get<MeResponse>("/v1/auth/me");
      setMe(data);
      setStatus("authed");
      return data;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setMe(null);
        setStatus("anon");
        return null;
      }
      throw err;
    }
  }

  async function signOut() {
    try {
      await api.post("/v1/auth/sign-out", {});
    } finally {
      setMe(null);
      setStatus("anon");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <AuthContext.Provider value={{ status, me, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
